import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ConoceColombiaPage.css';
import Header from './Header';
import AnimatedBackground from './AnimatedBackground';
import {
  ALEJA_EXPERIENCES,
  PERSONALIZED_TRIP,
  PIPELINE_DESTINATIONS,
  TAPIRES_PIPELINE,
  TIENDA_PIPELINE,
  VIAJES_CALENDARIO_2026,
  calendarStatusLabel,
  formatCop,
  buildAlejaWhatsAppUrl,
  buildPersonalizedMailto,
  pipelineStatusLabel,
  pipelineStatusColor,
} from '../constants/conoceColombia';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const FALLBACK_IMAGE = '/images/hero-viajes-colombia.png';

const ConoceColombiaPage = () => {
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationTrip, setRegistrationTrip] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    comments: ''
  });
  const [providerForm, setProviderForm] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    rnt: '',
    planSummary: ''
  });
  const [providerStatus, setProviderStatus] = useState(null);
  const [showProviderForm, setShowProviderForm] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openAlejaWhatsApp = (experience, e) => {
    e?.stopPropagation?.();
    window.open(buildAlejaWhatsAppUrl(experience), '_blank', 'noopener,noreferrer');
  };

  const openPersonalizedForm = (trip) => {
    setRegistrationTrip(trip);
    setSelectedExperience(null);
    setShowRegistration(true);
  };

  const handlePersonalizedSubmit = (e) => {
    e.preventDefault();
    window.location.href = buildPersonalizedMailto(form);
  };

  const handleProviderSubmit = async (e) => {
    e.preventDefault();
    setProviderStatus('sending');
    try {
      const res = await axios.post(`${API_URL}/conoce-colombia-providers`, providerForm);
      const code = res.data?.data?.leadCode || res.data?.leadCode;
      setProviderStatus('success');
      setProviderForm({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        website: '',
        rnt: '',
        planSummary: ''
      });
      if (code) {
        // eslint-disable-next-line no-alert
        alert(`¡Gracias! Registramos tu propuesta (${code}). Te contactaremos pronto.`);
      }
    } catch (err) {
      setProviderStatus('error');
      // eslint-disable-next-line no-alert
      alert(err.response?.data?.message || 'No se pudo enviar. Escribe a ventas@javiland.com');
    }
  };

  const renderExperienceCard = (item, { onPrimary, primaryLabel, badge } = {}) => (
    <article
      key={item.id}
      className={`trip-card ${item.isPersonalized ? 'trip-card-personalized' : ''} ${item.pipelineStatus ? 'trip-card-pipeline' : ''}`}
      onClick={() => {
        if (item.pipelineStatus) return;
        if (item.isPersonalized) openPersonalizedForm(item);
        else setSelectedExperience(item);
      }}
    >
      <div className="trip-image">
        <img src={item.image} alt={item.title} onError={(ev) => { ev.target.src = FALLBACK_IMAGE; }} />
        {badge && <span className="trip-package-badge">{badge}</span>}
        {item.slots && (
          <span className="trip-slots-badge">{item.slots} cupos</span>
        )}
        {item.pipelineStatus && (
          <span
            className="trip-pipeline-badge"
            style={{ backgroundColor: pipelineStatusColor(item.pipelineStatus) }}
          >
            {pipelineStatusLabel(item.pipelineStatus)}
          </span>
        )}
      </div>
      <div className="trip-content">
        {item.subtitle && <p className="trip-subtitle">{item.subtitle}</p>}
        <h3 className="trip-title">{item.title}</h3>
        {item.dates && (
          <p className="trip-dates">
            <i className="fas fa-calendar-alt" aria-hidden />
            {item.dates}
          </p>
        )}
        {item.datesNote && (
          <p className="trip-dates trip-dates--muted">
            <i className="fas fa-calendar-alt" aria-hidden />
            {item.datesNote}
          </p>
        )}
        <p className="trip-location">
          <i className="fas fa-map-marker-alt" aria-hidden />
          {item.location}
        </p>
        {item.priceCop != null && (
          <p className="trip-price">{formatCop(item.priceCop)}</p>
        )}
        {item.priceCop == null && item.priceFromCop != null && (
          <p className="trip-price">Desde {formatCop(item.priceFromCop)}</p>
        )}
        {item.description && <p className="trip-description">{item.description}</p>}
        <ul className="trip-highlights">
          {(item.highlights || []).map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
        {!item.pipelineStatus && (
          <button
            type="button"
            className="trip-cta"
            onClick={(e) => {
              e.stopPropagation();
              if (onPrimary) onPrimary(item, e);
              else if (item.isPersonalized) openPersonalizedForm(item);
              else setSelectedExperience(item);
            }}
          >
            {primaryLabel || (item.isPersonalized ? 'Solicitar cotización' : 'Más información')}
            <i className="fas fa-arrow-right" aria-hidden />
          </button>
        )}
      </div>
    </article>
  );

  return (
    <div className="conoce-colombia-page">
      <Header />
      <AnimatedBackground particleCount={15} staticCount={4} />

      <section
        className="conoce-colombia-hero"
        style={{
          background: `linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.65) 100%), url(${process.env.PUBLIC_URL || ''}/images/hero-conoce-colombia-4destinos.png) center/cover no-repeat, url(${process.env.PUBLIC_URL || ''}/images/hero-viajes-colombia.png) center/cover no-repeat`,
          backgroundColor: '#0a0a0a'
        }}
      >
        <div className="cc-hero-copy-centered">
          <span className="cc-tag">JAVILAND</span>
          <h1 className="cc-headline">Conoce las tierras colombianas</h1>
          <p className="cc-narrative">
            Ecoturismo consciente con expediciones confirmadas y proyectos propios en validación.
            Reserva con operadores aliados a través del ecosistema Javiland.
          </p>
          <button
            type="button"
            className="cc-cta"
            onClick={() => document.getElementById('cc-calendario')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Ver calendario 2026
          </button>
        </div>
      </section>

      <main className="conoce-colombia-main">
        <div className="container">
          <section className="cc-section cc-section--calendar" id="cc-calendario" aria-labelledby="cc-calendario-title">
            <h2 id="cc-calendario-title">Calendario de viajes 2026</h2>
            <p className="cc-packages-intro">
              Fechas confirmadas y próximos lanzamientos hasta fin de año. Las expediciones disponibles se reservan con el operador aliado vía Javiland.
            </p>
            <div className="cc-calendar">
              {VIAJES_CALENDARIO_2026.map((block) => (
                <div key={block.monthLabel} className="cc-calendar-month">
                  <h3 className="cc-calendar-month-title">{block.monthLabel}</h3>
                  <ul className="cc-calendar-list">
                    {block.items.map((row) => (
                      <li key={row.id} className={`cc-calendar-row cc-calendar-row--${row.status}`}>
                        <div className="cc-calendar-dates">
                          <i className="fas fa-calendar-alt" aria-hidden />
                          {row.dates}
                        </div>
                        <div className="cc-calendar-body">
                          <strong>{row.title}</strong>
                          <span className="cc-calendar-operator">{row.operator}</span>
                          {row.priceCop != null && (
                            <span className="cc-calendar-price">{formatCop(row.priceCop)}</span>
                          )}
                          {row.slots != null && (
                            <span className="cc-calendar-slots">{row.slots} cupos</span>
                          )}
                        </div>
                        <span className={`cc-calendar-status cc-calendar-status--${row.status}`}>
                          {calendarStatusLabel(row.status)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section className="cc-section" id="cc-disponibles" aria-labelledby="cc-disponibles-title">
            <h2 id="cc-disponibles-title">Disponibles ahora</h2>
            <p className="cc-packages-intro">
              Paquetes listos para reservar. Leads trazables vía Javiland — operación a cargo del operador certificado.
            </p>
            <div className="trips-grid">
              {ALEJA_EXPERIENCES.map((item) =>
                renderExperienceCard(item, {
                  badge: item.dates,
                  primaryLabel: 'Reservar por WhatsApp',
                  onPrimary: openAlejaWhatsApp
                })
              )}
              {renderExperienceCard(PERSONALIZED_TRIP, {
                badge: 'A tu medida',
                primaryLabel: 'Solicitar cotización'
              })}
            </div>
          </section>

          <section className="cc-section cc-section--pipeline" id="cc-pipeline" aria-labelledby="cc-pipeline-title">
            <h2 id="cc-pipeline-title">En el horizonte</h2>
            <p className="cc-packages-intro">
              Destinos propios Javiland y aliados en preparación — visibles mientras cerramos fechas, catálogo o precio.
            </p>

            <h3 className="cc-subsection-title">Destinos</h3>
            <div className="trips-grid trips-grid--pipeline">
              {PIPELINE_DESTINATIONS.map((item) => renderExperienceCard(item))}
              {TAPIRES_PIPELINE.map((item) => renderExperienceCard(item))}
            </div>

            <h3 className="cc-subsection-title">Tienda Javiland</h3>
            <p className="cc-packages-intro cc-packages-intro--tight">
              Próximos lanzamientos en la tienda del ecosistema — joyería artesanal, naturales y más.
            </p>
            <div className="trips-grid trips-grid--pipeline">
              {TIENDA_PIPELINE.map((item) => renderExperienceCard(item))}
            </div>
          </section>

          <section className="cc-section cc-section--provider" id="cc-proveedor" aria-labelledby="cc-proveedor-title">
            <button
              type="button"
              className="cc-provider-toggle"
              id="cc-proveedor-title"
              aria-expanded={showProviderForm}
              aria-controls="cc-proveedor-form"
              onClick={() => setShowProviderForm((open) => !open)}
            >
              <span>¿Eres proveedor de turismo?</span>
              <i className={`fas fa-chevron-${showProviderForm ? 'up' : 'down'}`} aria-hidden />
            </button>
            {showProviderForm && (
              <>
            <p className="cc-packages-intro">
              Si tienes un plan, ruta o agencia con operación clara (RNT u homólogo), cuéntanos.
              Evaluamos sumarte a Conoce Colombia para comercializar tu experiencia en Javiland.
            </p>
            <form id="cc-proveedor-form" className="cc-provider-form" onSubmit={handleProviderSubmit}>
              <div className="cc-provider-grid">
                <div className="form-group">
                  <label htmlFor="ccp-company">Agencia / operador</label>
                  <input
                    id="ccp-company"
                    required
                    value={providerForm.companyName}
                    onChange={(e) => setProviderForm((f) => ({ ...f, companyName: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ccp-contact">Persona de contacto</label>
                  <input
                    id="ccp-contact"
                    required
                    value={providerForm.contactName}
                    onChange={(e) => setProviderForm((f) => ({ ...f, contactName: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ccp-email">Email</label>
                  <input
                    id="ccp-email"
                    type="email"
                    required
                    value={providerForm.email}
                    onChange={(e) => setProviderForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ccp-phone">Teléfono / WhatsApp</label>
                  <input
                    id="ccp-phone"
                    value={providerForm.phone}
                    onChange={(e) => setProviderForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ccp-web">Sitio web o Instagram</label>
                  <input
                    id="ccp-web"
                    placeholder="https://…"
                    value={providerForm.website}
                    onChange={(e) => setProviderForm((f) => ({ ...f, website: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ccp-rnt">RNT (si aplica)</label>
                  <input
                    id="ccp-rnt"
                    value={providerForm.rnt}
                    onChange={(e) => setProviderForm((f) => ({ ...f, rnt: e.target.value }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="ccp-plan">Describe tu plan o experiencia</label>
                <textarea
                  id="ccp-plan"
                  rows="4"
                  required
                  placeholder="Destino, duración, precio orientativo, cupos, qué incluye…"
                  value={providerForm.planSummary}
                  onChange={(e) => setProviderForm((f) => ({ ...f, planSummary: e.target.value }))}
                />
              </div>
              <button type="submit" className="cc-provider-submit" disabled={providerStatus === 'sending'}>
                {providerStatus === 'sending' ? 'Enviando…' : 'Enviar propuesta'}
              </button>
            </form>
              </>
            )}
          </section>
        </div>
      </main>

      {selectedExperience && !selectedExperience.pipelineStatus && (
        <div className="trip-modal-overlay" onClick={() => setSelectedExperience(null)}>
          <div className="trip-modal trip-modal-plans" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button type="button" className="modal-close" onClick={() => setSelectedExperience(null)} aria-label="Cerrar">
              <i className="fas fa-times" aria-hidden />
            </button>
            <div className="trip-modal-content">
              <h2 className="trip-modal-title">{selectedExperience.title}</h2>
              {selectedExperience.subtitle && (
                <p className="trip-modal-subtitle">{selectedExperience.subtitle}</p>
              )}
              <div className="trip-modal-meta">
                {selectedExperience.dates && (
                  <span><i className="fas fa-calendar-alt" aria-hidden /> {selectedExperience.dates}</span>
                )}
                {selectedExperience.datesNote && (
                  <span><i className="fas fa-calendar-alt" aria-hidden /> {selectedExperience.datesNote}</span>
                )}
                <span><i className="fas fa-map-marker-alt" aria-hidden /> {selectedExperience.location}</span>
                {selectedExperience.duration && (
                  <span><i className="fas fa-clock" aria-hidden /> {selectedExperience.duration}</span>
                )}
              </div>
              {selectedExperience.priceCop != null && (
                <p className="trip-modal-price">
                  {formatCop(selectedExperience.priceCop)}
                  {selectedExperience.depositCop != null && (
                    <span className="trip-modal-deposit">
                      {' '}· Depósito 30%: {formatCop(selectedExperience.depositCop)}
                    </span>
                  )}
                </p>
              )}
              {selectedExperience.slots && (
                <p className="trip-modal-slots"><strong>{selectedExperience.slots} cupos</strong></p>
              )}
              {selectedExperience.certifiedDiversOnly && (
                <p className="trip-modal-requirement">Solo buzos certificados</p>
              )}
              {selectedExperience.operatorLine && (
                <p className="trip-modal-operator">{selectedExperience.operatorLine}</p>
              )}
              {selectedExperience.includes && (
                <>
                  <h4 className="trip-modal-list-title">Incluye</h4>
                  <ul className="trip-modal-highlights">
                    {selectedExperience.includes.map((line, i) => (
                      <li key={i}><i className="fas fa-check" aria-hidden /> {line}</li>
                    ))}
                  </ul>
                </>
              )}
              {selectedExperience.excludes && (
                <>
                  <h4 className="trip-modal-list-title">No incluye</h4>
                  <ul className="trip-modal-excludes">
                    {selectedExperience.excludes.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </>
              )}
              {selectedExperience.optional && (
                <p className="trip-modal-optional"><em>Opcional:</em> {selectedExperience.optional}</p>
              )}
              <ul className="trip-modal-highlights">
                {(selectedExperience.highlights || []).map((h, i) => (
                  <li key={i}><i className="fas fa-check" aria-hidden /> {h}</li>
                ))}
              </ul>
              {selectedExperience.providerId === 'aleja' && (
                <button
                  type="button"
                  className="register-btn register-btn--wa"
                  onClick={() => openAlejaWhatsApp(selectedExperience)}
                >
                  <i className="fab fa-whatsapp" aria-hidden />
                  Reservar por WhatsApp
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showRegistration && (
        <div
          className="registration-modal-overlay"
          onClick={() => { setShowRegistration(false); setRegistrationTrip(null); }}
        >
          <div className="registration-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button
              type="button"
              className="modal-close"
              onClick={() => { setShowRegistration(false); setRegistrationTrip(null); }}
              aria-label="Cerrar"
            >
              <i className="fas fa-times" aria-hidden />
            </button>
            <div className="registration-content">
              <h3>
                {registrationTrip
                  ? `Cotización: ${registrationTrip.title}`
                  : 'Acompañamiento personalizado'}
              </h3>
              <p>
                Cuéntanos qué buscas. Al enviar se abrirá tu correo a ventas@javiland.com con los datos.
              </p>
              <form className="registration-form" onSubmit={handlePersonalizedSubmit}>
                <div className="form-group">
                  <label htmlFor="cc-name">Nombre completo</label>
                  <input
                    id="cc-name"
                    type="text"
                    required
                    placeholder="Ej. John Smith"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cc-email">Email</label>
                  <input
                    id="cc-email"
                    type="email"
                    required
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cc-phone">Teléfono (con código de país)</label>
                  <input
                    id="cc-phone"
                    type="tel"
                    required
                    placeholder="Ej. +1 555 123 4567"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cc-country">País de origen</label>
                  <input
                    id="cc-country"
                    type="text"
                    placeholder="Ej. Estados Unidos"
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cc-comments">Comentarios o preferencias</label>
                  <textarea
                    id="cc-comments"
                    rows="3"
                    placeholder="Fechas tentativas, número de viajeros, presupuesto..."
                    value={form.comments}
                    onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
                  />
                </div>
                <button type="submit" className="pay-btn">
                  <i className="fas fa-paper-plane" aria-hidden />
                  Enviar solicitud
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConoceColombiaPage;
